/**
 * Advisor Chat Service for Command Center
 * Handles conversational AI advisor interactions and persistence
 */

import { db } from '../lib/firebase';
import {
    collection,
    addDoc,
    updateDoc,
    doc,
    query,
    where,
    orderBy,
    limit,
    getDocs,
    serverTimestamp
} from 'firebase/firestore';

const API_BASE = 'http://localhost:3001';
const COLLECTION_NAME = 'advisorChats';

/**
 * Enhanced system prompt for the AI advisor
 * This prompt helps the AI understand the context structure and provide better advice
 */
export const ADVISOR_SYSTEM_PROMPT = `You are an expert digital marketing advisor analyzing Meta/Google ad campaigns for a business.

## CRITICAL RULES - READ FIRST

1. **NEVER invent or hallucinate campaign data**. Only reference campaigns, metrics, IDs, and names that are EXPLICITLY provided in the context.
2. If the context shows no campaigns or empty data, acknowledge this clearly: "I don't see any campaign data in the current view. Please make sure campaigns are loaded."
3. If asked about a specific campaign that isn't in the context, say: "I don't have data for that campaign in the current context."
4. When the context shows campaigns, ONLY use the exact names and IDs provided - never make up campaign names like "Summer Sale" or IDs like "camp-001".

## CONTEXT STRUCTURE
You receive smart, token-efficient context based on the question type:

- **questionType**: The classified type of question (e.g., "funnel_impact", "roas_improvement", "budget_allocation")
- **overallHealth**: Quick health summary with active campaigns, spend, revenue, ROAS
- **userGoal**: The user's selected marketing objective (if set)

### Funnel Context (for funnel questions)
- **funnelContext.structure**: TOFU/MOFU/BOFU campaign counts, health, spend
- **funnelContext.analysis**: Funnel health issues, opportunities, spend distribution
- **funnelContext.relationships**: Campaign dependencies (retargeting chains, audience flow)

### Performance Context (for ROAS/performance questions)
- **performanceContext.byStage**: Performance metrics by awareness/consideration/conversion
- **performanceContext.trends**: Spend, revenue, ROAS trend directions
- **performanceContext.recommendations**: Budget optimization recommendations

### Budget Context (for budget questions)
- **budgetContext.distribution**: Budget split across funnel stages
- **budgetContext.topSpenders**: Highest budget campaigns with health status
- **budgetContext.recommendations**: Where to allocate/reduce budget

### Focused Campaigns (when specific campaigns mentioned)
- **focusedCampaigns**: Detailed data for specifically mentioned campaigns

### Campaign List (when available)
- **campaigns**: Array of campaign objects with metrics:
  - id, name, status, objective
  - dailyBudget: Daily budget amount
  - spend: Total spend for the period
  - revenue: Total revenue/value for the period
  - roas: Calculated ROAS (e.g., "2.45x")
  - impressions, clicks, conversions

## FUNNEL TERMINOLOGY
- **TOFU (Top of Funnel)**: Awareness campaigns (reach, impressions, video views)
- **MOFU (Middle of Funnel)**: Consideration campaigns (traffic, engagement, leads)
- **BOFU (Bottom of Funnel)**: Conversion campaigns (purchases, sales, ROAS-focused)

## KEY PRINCIPLES

### For Funnel Questions:
1. TOFU campaigns fill retargeting pools for MOFU/BOFU
2. Disabling awareness campaigns reduces future conversion audiences within 7-14 days
3. Healthy funnel ratio: TOFU spend > MOFU spend > BOFU spend (roughly 40/30/30)
4. Look at funnelContext.relationships to identify campaign dependencies

### For ROAS Questions:
1. Consider attribution windows and cross-campaign influence
2. Platform-reported ROAS may differ from actual ROAS
3. Look at budget reallocation opportunities from underperformers to winners
4. BOFU ROAS is most important for direct revenue

### For Budget Questions:
1. Shift budget from critical health campaigns to excellent ones
2. BOFU-heavy spend can exhaust audiences over time
3. Scale winners gradually (20-50% increases)
4. Maintain TOFU investment to replenish audience pools

### For Campaign Actions (pause/enable):
1. Always consider funnel dependencies before pausing
2. Pausing TOFU affects BOFU performance within 1-2 weeks
3. Check campaign health before recommending pause
4. Excellent performers should rarely be paused

## RESPONSE GUIDELINES

1. **Be specific**: Reference actual campaign names, numbers, and metrics from context
2. **Explain the "why"**: Don't just say what to do, explain the reasoning
3. **Consider dependencies**: Especially for pause/scale decisions
4. **Prioritize actions**: Recommend the highest-impact action first
5. **Suggest follow-ups**: The context includes suggestedFollowUps - use these naturally
6. **Be concise**: Users are busy - get to the point quickly
7. **Propose actions when appropriate**: If you recommend pausing/scaling, offer a specific action

## CLARIFYING USER INTENT - QUICK WINS VS STRATEGIC

When a user asks a question that could be answered with either tactical quick wins OR a more strategic long-term view, and you CANNOT clearly infer their intent from the question, follow up by asking:

**"Are you looking for quick wins you can implement today, or would you prefer a more strategic long-term view?"**

Examples of when to ASK (intent unclear):
- "How can I improve my campaigns?" → Could be quick fixes OR strategic overhaul
- "What should I focus on?" → Could be immediate actions OR long-term planning
- "How do I get better results?" → Ambiguous timeframe

Examples of when NOT to ask (intent is clear):
- "What can I do RIGHT NOW to improve ROAS?" → Clearly wants quick wins
- "Help me build a long-term growth strategy" → Clearly wants strategic view
- "Should I pause this campaign today?" → Immediate tactical decision
- "What's my funnel health?" → Diagnostic question, just answer it
- "Why is my CPA so high?" → Diagnostic question, just answer it
- Questions about specific campaigns or metrics → Just provide the analysis

Only ask this clarifying question ONCE at the start of a conversation thread. If the user has already indicated their preference (quick wins vs strategic) earlier in the conversation, remember it and tailor subsequent responses accordingly.

## RESPONSE FORMAT

When recommending actions, you can include a proposed action object:
- For budget changes: Include campaignId, newBudget, previousBudget
- For pause/resume: Include campaignId and campaign name

Keep responses focused and actionable. Avoid generic marketing advice - use the specific data provided.

## HANDLING MISSING DATA

If context shows:
- **Empty campaigns array or no campaignIndex**: Say "I don't have campaign data loaded. Please ensure your campaigns are synced."
- **No funnel data**: Provide general funnel advice but clarify you can't see their specific setup
- **No metrics**: Acknowledge the data gap before giving general advice
- **overallHealth.activeCampaigns = 0**: "You don't have any active campaigns right now."
- **warning = 'METRICS_NOT_LOADED'**: Tell the user their campaigns are connected but the performance metrics show $0. Suggest they click the "Refresh" button in the Command Center or wait for the next sync to pull fresh metrics from their ad platform.
- **hasIndividualCampaignMetrics = false but hasMetrics = true**: The aggregate totals are available but individual campaign breakdowns are not. Use the aggregate data (overallHealth) for advice, but note you can't drill into specific campaigns yet.

REMEMBER: It's better to say "I don't have that data" than to make something up. Users trust accurate information over fabricated helpfulness.`;

/**
 * Goal presets for quick selection
 */
export const GOAL_PRESETS = [
    {
        id: 'scale_revenue',
        label: 'Scale Revenue',
        description: 'Grow revenue while maintaining profitability',
        objective: 'Scale revenue',
        targetMetric: 'revenue',
        constraints: ['maintain ROAS above 2x']
    },
    {
        id: 'improve_roas',
        label: 'Improve ROAS',
        description: 'Optimize campaigns for better return on ad spend',
        objective: 'Improve ROAS',
        targetMetric: 'roas',
        constraints: ['reduce wasted spend']
    },
    {
        id: 'reduce_cpa',
        label: 'Lower Acquisition Cost',
        description: 'Reduce the cost per acquisition/conversion',
        objective: 'Lower CPA',
        targetMetric: 'cpa',
        constraints: ['maintain conversion volume']
    },
    {
        id: 'increase_reach',
        label: 'Increase Reach',
        description: 'Expand audience reach and brand awareness',
        objective: 'Increase reach',
        targetMetric: 'impressions',
        constraints: ['stay within budget']
    },
    {
        id: 'test_creatives',
        label: 'Test New Creatives',
        description: 'Find winning creative concepts',
        objective: 'Test creatives',
        targetMetric: 'ctr',
        constraints: ['allocate test budget']
    }
];

/**
 * Send a message to the AI advisor and get a response
 * @param {Object} params - Request parameters
 * @param {string} params.message - User's message
 * @param {Array} params.conversationHistory - Previous messages in the conversation
 * @param {Object} params.context - Campaign/metrics context (smart context from smartContextBuilder)
 * @param {Object} params.userGoal - User's marketing goal if set
 * @returns {Promise<Object>} - AI response with message, suggestions, proposedAction, dataCards
 */
export const sendAdvisorMessage = async ({ message, conversationHistory = [], context, userGoal }) => {
    const response = await fetch(`${API_BASE}/api/ai/advisor-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            message,
            conversationHistory,
            context,
            userGoal,
            // Include the system prompt for the backend to use
            systemPrompt: ADVISOR_SYSTEM_PROMPT
        })
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to get advisor response');
    }

    return response.json();
};

/**
 * Execute a campaign action (budget change, pause, resume)
 * @param {Object} action - The action to execute
 * @param {string} action.type - 'budget_change' | 'pause' | 'resume'
 * @param {string} action.campaignId - Campaign ID
 * @param {Object} action.params - Action parameters
 * @returns {Promise<Object>} - Result with success status and undo info
 */
export const executeAction = async (action) => {
    const { type, campaignId, params } = action;

    let endpoint;
    let method = 'POST';
    let body = {};

    switch (type) {
        case 'budget_change':
            endpoint = `/api/campaigns/${campaignId}/budget`;
            body = { dailyBudget: params.newBudget };
            break;
        case 'pause':
            endpoint = `/api/campaigns/${campaignId}/pause`;
            break;
        case 'resume':
            endpoint = `/api/campaigns/${campaignId}/resume`;
            break;
        default:
            throw new Error(`Unknown action type: ${type}`);
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `Failed to execute ${type} action`);
    }

    const result = await response.json();

    return {
        success: true,
        result,
        undoAction: {
            type: type === 'pause' ? 'resume' : type === 'resume' ? 'pause' : 'budget_change',
            campaignId,
            params: type === 'budget_change' ? { newBudget: params.previousBudget } : {}
        }
    };
};

/**
 * Undo a previously executed action
 * @param {Object} undoAction - The undo action details
 * @returns {Promise<Object>} - Result of the undo operation
 */
export const undoAction = async (undoAction) => {
    return executeAction(undoAction);
};

/**
 * Save or update an advisor chat session
 * @param {Object} chatData - Chat session data
 * @param {string} userId - User ID
 * @param {string} brandId - Brand ID
 * @returns {Promise<string>} - Chat document ID
 */
export const saveAdvisorChat = async (chatData, userId, brandId) => {
    try {
        const { id, ...dataToSave } = chatData;

        const chatDoc = {
            ...dataToSave,
            userId,
            brandId,
            updatedAt: serverTimestamp(),
            createdAt: chatData.createdAt || serverTimestamp()
        };

        if (id) {
            // Update existing chat
            const chatRef = doc(db, COLLECTION_NAME, id);
            await updateDoc(chatRef, {
                ...dataToSave,
                updatedAt: serverTimestamp()
            });
            return id;
        } else {
            // Create new chat
            const docRef = await addDoc(collection(db, COLLECTION_NAME), chatDoc);
            return docRef.id;
        }
    } catch (error) {
        console.error('Error saving advisor chat:', error);
        throw error;
    }
};

/**
 * Load the most recent advisor chat for a brand
 * @param {string} userId - User ID
 * @param {string} brandId - Brand ID
 * @returns {Promise<Object|null>} - Chat data or null if none exists
 */
export const loadLatestAdvisorChat = async (userId, brandId) => {
    try {
        const chatsRef = collection(db, COLLECTION_NAME);
        const q = query(
            chatsRef,
            where('userId', '==', userId),
            where('brandId', '==', brandId),
            orderBy('updatedAt', 'desc'),
            limit(1)
        );

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return null;
        }

        const chatDoc = querySnapshot.docs[0];
        return {
            id: chatDoc.id,
            ...chatDoc.data()
        };
    } catch (error) {
        console.error('Error loading advisor chat:', error);
        throw error;
    }
};

/**
 * Prepare chat data for saving
 * @param {Object} chatState - Current chat state
 * @returns {Object} - Serializable chat data
 */
export const prepareChatDataForSave = (chatState) => {
    const {
        messages = [],
        userGoal = null,
        goalSetAt = null
    } = chatState;

    // Serialize messages
    const serializedMessages = messages.map(msg => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp instanceof Date
            ? msg.timestamp.toISOString()
            : msg.timestamp || new Date().toISOString(),
        suggestions: msg.suggestions || null,
        dataCards: msg.dataCards || null,
        proposedAction: msg.proposedAction || null,
        executedAction: msg.executedAction || null
    }));

    // Generate title from first user message or goal
    let title = 'Advisor Chat';
    if (userGoal?.objective) {
        title = `Goal: ${userGoal.objective}`;
    } else if (messages.length > 0) {
        const firstUserMsg = messages.find(m => m.role === 'user');
        if (firstUserMsg) {
            title = firstUserMsg.content.substring(0, 50) + (firstUserMsg.content.length > 50 ? '...' : '');
        }
    }

    return {
        title,
        messages: serializedMessages,
        userGoal,
        goalSetAt,
        messageCount: serializedMessages.length
    };
};

/**
 * Auto-save advisor chat with debouncing
 */
let autoSaveTimeout = null;

export const scheduleAutoSave = (chatState, userId, brandId, chatId, onSaveComplete) => {
    if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
    }

    autoSaveTimeout = setTimeout(async () => {
        try {
            const chatData = prepareChatDataForSave(chatState);
            const savedChatId = await saveAdvisorChat({ ...chatData, id: chatId }, userId, brandId);

            if (onSaveComplete) {
                onSaveComplete(savedChatId);
            }
            console.log('Advisor chat auto-saved:', savedChatId);
        } catch (error) {
            console.error('Advisor chat auto-save failed:', error);
        }
    }, 2000);
};

/**
 * Generate a unique message ID
 */
export const generateMessageId = () => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create a user message object
 */
export const createUserMessage = (content) => ({
    id: generateMessageId(),
    role: 'user',
    content,
    timestamp: new Date().toISOString()
});

/**
 * Create an assistant message object from API response
 */
export const createAssistantMessage = (apiResponse) => ({
    id: generateMessageId(),
    role: 'assistant',
    content: apiResponse.response,
    timestamp: new Date().toISOString(),
    suggestions: apiResponse.suggestions || [],
    dataCards: apiResponse.dataCards || null,
    proposedAction: apiResponse.proposedAction || null
});

/**
 * Summarize conversation history for context (when too long)
 * @param {Array} messages - Full message history
 * @param {number} keepRecent - Number of recent messages to keep in full
 * @returns {Array} - Condensed message history
 */
export const summarizeHistory = (messages, keepRecent = 10) => {
    if (messages.length <= keepRecent) {
        return messages;
    }

    const olderMessages = messages.slice(0, -keepRecent);
    const recentMessages = messages.slice(-keepRecent);

    // Create a summary of older messages
    const topics = new Set();
    const actions = [];

    olderMessages.forEach(msg => {
        if (msg.executedAction) {
            actions.push(msg.executedAction.description);
        }
        // Extract key topics from user messages
        if (msg.role === 'user') {
            const words = msg.content.toLowerCase();
            if (words.includes('budget')) topics.add('budget');
            if (words.includes('roas') || words.includes('return')) topics.add('ROAS');
            if (words.includes('scale') || words.includes('grow')) topics.add('scaling');
            if (words.includes('pause') || words.includes('stop')) topics.add('pausing');
            if (words.includes('creative')) topics.add('creatives');
            if (words.includes('audience')) topics.add('audiences');
        }
    });

    const summaryContent = `[Earlier in this conversation: Discussed ${Array.from(topics).join(', ') || 'campaign performance'}. ${actions.length > 0 ? `Actions taken: ${actions.join('; ')}` : ''}]`;

    return [
        { id: 'summary', role: 'system', content: summaryContent, timestamp: olderMessages[0]?.timestamp },
        ...recentMessages
    ];
};

export default {
    sendAdvisorMessage,
    executeAction,
    undoAction,
    saveAdvisorChat,
    loadLatestAdvisorChat,
    prepareChatDataForSave,
    scheduleAutoSave,
    generateMessageId,
    createUserMessage,
    createAssistantMessage,
    summarizeHistory,
    GOAL_PRESETS
};
